import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter } from "../../../../resources/constants";
import { connect } from "react-redux";
import SearchSelect from "../../../common/select/SearchSelect";

const STATUS_COLOR = { Active: "info", Completed: "success", Defaulted: "danger", Cancelled: "secondary" };

function InstallmentReport() {
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [semesterList, setSemesterList] = useState([]);
    const [summaryStats, setSummaryStats] = useState({
        TotalPlans: 0, ActiveCount: 0, CompletedCount: 0, DefaultedCount: 0, CancelledCount: 0,
        TotalAmountExpected: 0, TotalAmountPaid: 0, TotalBalanceRemaining: 0,
    });
    const [filters, setFilters] = useState({ SemesterCode: "" });
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "S/N", field: "sn" },
            { label: "Student ID", field: "StudentID" },
            { label: "Student Name", field: "StudentName" },
            { label: "Invoice No", field: "InvoiceNo" },
            { label: "Total Amount", field: "TotalAmount" },
            { label: "Amount Paid", field: "AmountPaid" },
            { label: "Balance", field: "Balance" },
            { label: "Installments", field: "InstallmentProgress" },
            { label: "Status", field: "Status" },
        ],
        rows: [],
    });

    const semesterOptions = useMemo(() => semesterList.map(s => ({
        value: s.SemesterCode,
        label: (s.SemesterName || s.SemesterCode) + (s.IsCurrent === 1 ? " (Current)" : ""),
    })), [semesterList]);

    const getSemesters = async () => {
        const result = await api.get("staff/registration/semester/list");
        if (result.success && result.data) {
            const data = Array.isArray(result.data) ? result.data : result.data.data || [];
            setSemesterList(data);
            const current = data.find(s => s.IsCurrent === 1);
            setFilters(f => ({ ...f, SemesterCode: current?.SemesterCode || data[0]?.SemesterCode || "" }));
        }
    };

    const getReportData = async () => {
        if (!filters.SemesterCode) { setIsLoading(false); return; }
        setIsLoading(true);
        const result = await api.get(`staff/finance/invoices/installment-report?semester=${filters.SemesterCode}`);
        if (result.success && result.data?.data) {
            const { summary, rows } = result.data.data;
            setSummaryStats(summary || {});
            setReportData(rows || []);
            buildTable(rows || []);
        }
        setIsLoading(false);
    };

    const buildTable = (data) => {
        const rows = data.map((item, i) => ({
            sn: i + 1,
            StudentID: item.StudentID,
            StudentName: <span className="fw-bold">{item.StudentName}</span>,
            InvoiceNo: <span className="text-muted">#{item.InvoiceNumber}</span>,
            TotalAmount: <span>{currencyConverter(item.TotalAmount)}</span>,
            AmountPaid: <span className="text-success fw-bold">{currencyConverter(item.AmountPaid)}</span>,
            Balance: <span className="text-danger fw-bold">{currencyConverter(item.BalanceRemaining)}</span>,
            InstallmentProgress: <span className="fw-bold">{item.InstallmentsPaid}/{item.TotalInstallments}</span>,
            Status: <span className={`badge badge-light-${STATUS_COLOR[item.PlanStatus] || "secondary"}`}>{item.PlanStatus}</span>,
        }));
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        const headers = ["Student ID", "Student Name", "Invoice No", "Total Amount", "Amount Paid", "Balance", "Installments Paid", "Total Installments", "Status"];
        const rows = reportData.map(item => [
            item.StudentID, item.StudentName, item.InvoiceNumber,
            item.TotalAmount, item.AmountPaid, item.BalanceRemaining,
            item.InstallmentsPaid, item.TotalInstallments, item.PlanStatus,
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `installment_report_${filters.SemesterCode}_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    useEffect(() => { getSemesters(); }, []);
    useEffect(() => { if (filters.SemesterCode) getReportData(); }, [filters.SemesterCode]);

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="Installment Plan Students" items={["Finance", "Invoices", "Installment Report"]} />
            <div className="flex-column-fluid">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label required">Semester</label>
                                <SearchSelect
                                    id="SemesterCode"
                                    value={semesterOptions.find(o => o.value === filters.SemesterCode) || null}
                                    options={semesterOptions}
                                    onChange={(s) => setFilters(f => ({ ...f, SemesterCode: s?.value || "" }))}
                                    placeholder="Select Semester"
                                    isClearable={false}
                                />
                            </div>
                            <div className="col-md-7 text-end">
                                <button className="btn btn-light-primary" onClick={exportToCSV} disabled={!reportData.length}>
                                    <i className="fa fa-download me-2"></i>Export CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {filters.SemesterCode && (
                    <>
                        <div className="row g-4 mb-4">
                            {[
                                { label: "Total Plans", value: summaryStats.TotalPlans, color: "primary", icon: "fa-list-ol", isCount: true },
                                { label: "Active", value: summaryStats.ActiveCount, color: "info", icon: "fa-spinner", isCount: true },
                                { label: "Defaulted", value: summaryStats.DefaultedCount, color: "danger", icon: "fa-exclamation-triangle", isCount: true },
                                { label: "Completed", value: summaryStats.CompletedCount, color: "success", icon: "fa-check-circle", isCount: true },
                            ].map((c, i) => (
                                <div className="col-md-3" key={i}>
                                    <div className={`card bg-light-${c.color}`}>
                                        <div className="card-body py-4">
                                            <div className="d-flex align-items-center">
                                                <div className="symbol symbol-50px me-3">
                                                    <span className={`symbol-label bg-${c.color}`}>
                                                        <i className={`fa ${c.icon} text-white fs-3`}></i>
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="fs-2 fw-bold">{c.value || 0}</div>
                                                    <div className="text-muted small">{c.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="row g-4 mb-4">
                            {[
                                { label: "Total Expected", value: summaryStats.TotalAmountExpected, color: "primary" },
                                { label: "Total Paid", value: summaryStats.TotalAmountPaid, color: "success" },
                                { label: "Total Remaining", value: summaryStats.TotalBalanceRemaining, color: "danger" },
                            ].map((c, i) => (
                                <div className="col-md-4" key={i}>
                                    <div className={`card border-${c.color}`}>
                                        <div className="card-body py-3 text-center">
                                            <div className={`fs-5 fw-bold text-${c.color}`}>{currencyConverter(c.value || 0)}</div>
                                            <div className="text-muted small">{c.label}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Installment Plans</h3>
                                <div className="card-toolbar">
                                    <span className="text-muted fs-7">{reportData.length} plans</span>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                {reportData.length > 0 ? <AGTable data={datatable} /> : (
                                    <div className="text-center py-10">
                                        <p className="text-muted">No installment plans found for this semester</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default connect()(InstallmentReport);
